import { useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material"
import Icon from "../../../../Icon/Icon"
import { EGSwitch } from "../../../../MainComponents/Switch/Switch";
import { HelpIcon } from "../../../../MainComponents/HelpIcon/HelpIcon";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import EGFileUpload from "../../../../MainComponents/FileUpload/EGFileUpload/EGFileUpload";
import { CommentFiles } from "./CommentFiles";
import { SubmitButtons } from "./CloseReport";
import { RelatedReportsClosureSection } from "./RelatedReportsClosureSection";
import { enqueueSnackbar } from "notistack";
import { createAttachment, updateStatusCloseDetails } from "../../../../../apis";
import { useTranslation } from "react-i18next";

export const ClosedFounded = ({
  id,
  reportDetail,
  closeDate,
  reportExpired,
  reportRender,
  editClosed,
  setEditClosed,
}) => {

  const [t] = useTranslation("report");
  const theme = useTheme();

  const [followUp, setFollowUp] = useState(reportDetail?.status?.details?.followup || false);

  const [observations, setObservations] = useState(reportDetail?.status?.details?.observations || "");
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState(reportDetail?.status?.details?.files || []);
  const [isLoading, setIsLoading] = useState(false);

  // Estados para cerrar denuncias relacionadas
  const [closeLinkedReports, setCloseLinkedReports] = useState(false);
  const [closeAllReports, setCloseAllReports] = useState(true);
  const [selectedReports, setSelectedReports] = useState([]);

  const returnIsValid = () => {
    if (followUp && observations.trim() === "") return false;
    return true;
  }

  // Obtener los reportes a cerrar
  const getReportsToClose = () => {
    if (!closeLinkedReports) return [];
    if (closeAllReports) return reportDetail?.related?.map((r) => r.id_report) || [];
    return selectedReports;
  };

  const handleSubmit = async (e) => {

    setIsLoading(true);

    let files_id;
    let filesToSend = [];

    enqueueSnackbar(`${t("Conclusion.sending")}...`, { variant: "info" });

    try {
      if (files.length > 0) {
        for (const fileObj of files) {
          const { formData } = fileObj;
          const filesRes = await createAttachment(formData);
          files_id = filesRes.attachments_id;
          filesToSend.push({
            file: files_id,
            name: fileObj.file.name,
            ...(!!fileObj.comment && {comment: fileObj.comment})
          });
        }
      }

      // Add current files
      if (uploadedFiles && uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          filesToSend.push({
            file: file.file,
            name: file.name,
            ...(!!file.comment && {comment: file.comment})
          });
        });
      }

      const details = {
        another_causes: [],
        followup: followUp,
        closed: "founded",
        close_date: closeDate,
        causes: [],
        observations,
        files: filesToSend,
        expired_reports: reportExpired,
      };

      await updateStatusCloseDetails(id, details);

      // Cerrar las denuncias relacionadas si aplica
      const reportsToClose = getReportsToClose();
      if (reportsToClose.length > 0) {
        const closeDetails = {
          closed: "founded",
          close_date: closeDate,
          observations,
          files: filesToSend,
          followup: followUp,
          causes: [],
          another_causes: [],
        };

        // Cerrar cada reporte seleccionado
        const closePromises = reportsToClose.map((reportId) =>
          updateStatusCloseDetails(reportId, closeDetails)
        );

        await Promise.all(closePromises);

        enqueueSnackbar(
          `${t("Conclusion.sending_successful")} - ${reportsToClose.length} ${t("Conclusion.related_reports_closed") || "denuncias relacionadas cerradas"}`,
          { variant: "success" }
        );
      } else {
        enqueueSnackbar(`${t("Conclusion.sending_successful")}`, {
          variant: "success",
        });
      }

      reportRender();
      setIsLoading(false);
      setEditClosed(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      enqueueSnackbar(`${t("Conclusion.error_closing_reports") || "Error al cerrar las denuncias"}`, {
        variant: "error",
      });
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" marginBottom={2}>
        <Box marginRight={1}>
          <Icon name="bold_routing_2" size={24} color={theme.palette.magentaBliss.main} />
        </Box>
        <Typography fontWeight={600} color="blueGray700">
          {t("Conclusion.this_report_will_followedup_another_instance")}
        </Typography>
        <HelpIcon 
          text="Texto de ayuda" 
          sx={{ marginLeft: .5}} 
        />
        <EGSwitch
          checked={followUp}
          onChange={(e) => setFollowUp(e.target.checked)}
          id="hostile"
          size="small"
          sx={{
            marginLeft: 3.5
          }}
        />
      </Stack>

      <EGInput 
        label={t("Conclusion.observations_type_close")}
        placeholder={t("Conclusion.observations_type_close_placeholder")}
        required={followUp}
        multiline
        rows={4}
        value={observations}
        showHelpText={false}
        showSuccessValidation
        onChange={(e) => setObservations(e.target.value)}
        sx={{
          marginBottom: 2
        }}
        {...(followUp && { isValid: observations.length > 1 ? true : false })}
      />

      <Box marginBottom={2}>
        <EGFileUpload
          title={t("Conclusion.support_fiels")}
          files={files}
          setFiles={setFiles}
        />
      </Box>

      {
        (files.length > 0 || uploadedFiles.length > 0) &&
        <Stack border={`1px solid ${theme.palette.teal.main}`} borderRadius={1} marginBottom={2}>
          {
            files.length !== 0 &&
            <CommentFiles
              files={files}
              setFiles={setFiles}
              pathName="file.name"
              pathSize="file.size"
            />
          }

          {
            uploadedFiles.length !== 0 &&
            <CommentFiles
              files={uploadedFiles}
              setFiles={setUploadedFiles}
            />
          }
        </Stack>
      }

      {/* Sección para cerrar denuncias relacionadas */}
      <RelatedReportsClosureSection
        relatedReports={reportDetail?.related || []}
        closeLinkedReports={closeLinkedReports}
        setCloseLinkedReports={setCloseLinkedReports}
        closeAllReports={closeAllReports}
        setCloseAllReports={setCloseAllReports}
        selectedReports={selectedReports}
        setSelectedReports={setSelectedReports}
      />

      <SubmitButtons
        isLoading={isLoading}
        disabledSave={!returnIsValid()}
        onSave={handleSubmit}
        onCancel={() => setEditClosed(false)}
        hideCancel={!editClosed}
      />

    </>
  )
}
